import enMessages from '../locales/en.json'
import koMessages from '../locales/ko.json'

export type Locale = 'en' | 'ko'

const LOCALE_KEY = 'cs-locale'

const LOCALES: Record<Locale, Record<string, string>> = {
  en: enMessages as Record<string, string>,
  ko: koMessages as Record<string, string>,
}

let currentLocale: Locale = detectInitialLocale()

const translations: Record<string, string> = {}
Object.assign(translations, LOCALES[currentLocale])

function detectInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_KEY)
    if (stored === 'en' || stored === 'ko') return stored
  } catch {
    // localStorage unavailable (SSR / restricted env) — fall through.
  }

  const lang =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language.toLowerCase()
      : ''
  if (lang.startsWith('en')) return 'en'
  // Korean-first product: ko-* and any other locale default to Korean.
  return 'ko'
}

function interpolate(
  template: string,
  params: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  )
}

export function gettext(
  msgid: string,
  params?: Record<string, string | number>,
): string {
  const template = translations[msgid] ?? msgid
  return params ? interpolate(template, params) : template
}

export function ngettext(singular: string, plural: string, n: number): string {
  // Korean uses nplurals=1 (always the singular form); English pluralizes
  // for n !== 1. The lookup falls back to the form itself when untranslated.
  const form = currentLocale === 'ko' || n === 1 ? singular : plural
  return translations[form] ?? form
}

export function setLocale(locale: Locale): void {
  currentLocale = locale
  for (const key of Object.keys(translations)) delete translations[key]
  Object.assign(translations, LOCALES[locale])
  try {
    localStorage.setItem(LOCALE_KEY, locale)
  } catch {
    // localStorage unavailable — keep the in-memory switch only.
  }
}

export function getLocale(): Locale {
  return currentLocale
}
